import type { EnrichmentPlugin, Event } from "@amplitude/analytics-types";

/**
 * Shopify and Blotout EdgeTag identifiers stamped on Amplitude events so
 * session stitching can be studied from evidence (sc-33254). Observational
 * only: event properties, never setUserId, identify() or user properties.
 */
export type IdentifierEnvelope = {
  blotout_session_id?: string;
  blotout_user_id?: string;
  shopify_cart_token?: string;
  shopify_customer_id?: string;
  shopify_visitor_token?: string;
};

// Every source is optional and can throw (blocked storage, a half-loaded
// ShopifyAnalytics, a malformed cookie). An empty value counts as absent.
function read(source: () => unknown): string | undefined {
  try {
    const value = source();
    return value ? String(value) : undefined;
  } catch {
    return undefined;
  }
}

function readCookie(name: string): string | undefined {
  const prefix = `${name}=`;
  const cookie = document.cookie.split("; ").find((c) => c.startsWith(prefix));
  return cookie && decodeURIComponent(cookie.slice(prefix.length));
}

/**
 * Read on every event, never cached: the Blotout cookies are set after the
 * first event of a new visit. _shopify_y/_shopify_s are not read — Shopify
 * stopped setting them for new visitors on 2026-01-01; uniqToken covers
 * every visitor.
 */
export function readIdentifierEnvelope(): IdentifierEnvelope {
  const envelope: IdentifierEnvelope = {
    blotout_session_id: read(() => readCookie("tag_session")),
    blotout_user_id: read(() => readCookie("tag_user_id")),
    // "gid://shopify/Cart/<token>?key=<secret>" (the tablescape cart,
    // STOREFRONT_CART_LOCAL_STORAGE_KEY). <token> is the order's cart_token;
    // the ?key= part is a secret and must never be sent.
    shopify_cart_token: read(
      () =>
        window.localStorage
          .getItem("shopifyCartId")
          ?.match(/Cart\/([^?/]+)/)?.[1]
    ),
    shopify_customer_id: read(() => window.theme?.customer?.id),
    shopify_visitor_token: read(
      () => window.ShopifyAnalytics?.lib?.user?.().traits?.().uniqToken
    ),
  };

  // Absent identifiers are left out, never sent as "" or null: a missing key
  // is SQL NULL in the Snowflake export, while "" counts as present and joins.
  return Object.fromEntries(
    Object.entries(envelope).filter(([, value]) => value !== undefined)
  );
}

export const identifierEnrichmentPlugin: EnrichmentPlugin = {
  name: "op-identifier-envelope",
  type: "enrichment",
  async execute(event: Event) {
    // $identify / $groupidentify carry user properties, not event properties.
    if (!event.event_type.startsWith("$")) {
      event.event_properties = {
        ...readIdentifierEnvelope(),
        ...event.event_properties,
      };
    }
    return event;
  },
};
